import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";

import { metaAdsApi } from "api/meta-ads";
import { Layout } from "providers/Layout";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant
} from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Card } from "shared/components/Card";
import { Loader } from "shared/components/Loader";
import { IntegrationLogoIcon } from "views/settings-integration/components/IntegrationLogoIcon";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { getErrorMessage } from "utils/error";
import { MetaAdsConnection } from "types/landlord/meta-ads";

import { ConnectedPagesList } from "./components/ConnectedPagesList";
import { DisconnectMetaAdsModal } from "./components/DisconnectMetaAdsModal";
import {
  CALLBACK_REASON_MESSAGES,
  QUERY_META_ADS_STATUS,
  formatTokenExpiry
} from "./constants";

const ConnectedView = ({ status }: { status: MetaAdsConnection }) => (
  <div className="flex flex-col gap-6 max-w-[800px] w-full">
    <Card className="p-6">
      <p className="text-base font-bold text-content-1000">Account details</p>
      <div className="mt-4 grid grid-cols-2 gap-y-3 text-sm">
        <p className="text-content-400">Connected as</p>
        <p className="text-content-1000">
          {status.facebook_user_name ?? "Unknown"}
        </p>
        <p className="text-content-400">Long-lived token expires</p>
        <p className="text-content-1000">
          {formatTokenExpiry(status.token_expires_at)}
        </p>
      </div>
    </Card>

    <div>
      <p className="text-base font-bold text-content-1000 mb-3">
        Connected pages ({status.pages.length})
      </p>
      <ConnectedPagesList pages={status.pages} />
    </div>
  </div>
);

export const MetaAdsView = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  const { data: status, isLoading } = useQuery({
    queryKey: QUERY_META_ADS_STATUS,
    queryFn: metaAdsApi.getStatus
  });

  const { mutate: disconnect, isPending: isDisconnecting } = useMutation({
    mutationKey: ["META_ADS", "DELETE"],
    mutationFn: metaAdsApi.disconnect,
    onSuccess: () => {
      toast.success("Meta Lead Ads disconnected");
      queryClient.invalidateQueries({ queryKey: QUERY_META_ADS_STATUS });
      navigate("/settings/integration", { replace: true });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  useEffect(() => {
    const callbackStatus = searchParams.get("status");
    if (!callbackStatus) return;

    if (callbackStatus === "connected") {
      toast.success("Meta Lead Ads connected successfully");
      const next = new URLSearchParams(searchParams);
      next.delete("status");
      next.delete("reason");
      setSearchParams(next, { replace: true });
      return;
    }

    if (callbackStatus === "denied")
      toast.info("Meta Lead Ads connection was cancelled");
    else if (callbackStatus === "error") {
      const reason = searchParams.get("reason") ?? "";
      toast.error(
        CALLBACK_REASON_MESSAGES[reason] ?? "Meta Lead Ads connection failed"
      );
    }

    navigate("/settings/integration", { replace: true });
  }, [searchParams, setSearchParams, navigate]);

  useEffect(() => {
    if (isLoading) return;
    if (searchParams.get("status")) return;
    if (!status?.connected)
      navigate("/settings/integration", { replace: true });
  }, [isLoading, status, searchParams, navigate]);

  const renderBody = () => {
    if (isLoading || !status?.connected)
      return (
        <div className="flex justify-center py-10">
          <Loader />
        </div>
      );

    return <ConnectedView status={status} />;
  };

  return (
    <Layout
      header={
        <>
          <div className="flex items-center justify-between border-b border-alpha-40 px-3 md:px-6 pb-3">
            <BreadCrumbs
              crumbs={[
                { title: "Settings", href: "/settings" },
                { title: "Integration", href: "/settings/integration" },
                {
                  title: "Meta Lead Ads",
                  href: "/settings/integration/meta-ads"
                }
              ]}
            />
          </div>
          <div className="flex items-center justify-between px-6 py-4 border-b border-alpha-40">
            <div className="flex items-center gap-4">
              <IntegrationLogoIcon id={IntegrationId.META_ADS} size="SM" />
              <p className="text-xl font-bold text-content-1000">
                Meta Lead Ads
              </p>
              {status?.connected ? (
                <Badge variant={BadgeVariant.SUCCESS} size={BadgeSize.SM}>
                  Connected
                </Badge>
              ) : null}
            </div>
            {status?.connected ? (
              <Button
                variant={ButtonVariant.BASE}
                size={ButtonSize.SM}
                HugeIcon={Cancel01Icon}
                onClick={() => setShowDisconnectModal(true)}
                disabled={isDisconnecting}
              >
                Disconnect
              </Button>
            ) : null}
          </div>
        </>
      }
      footer={
        <div className="flex justify-end">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={() => navigate("/settings/integration")}
          >
            Back to integrations
          </Button>
        </div>
      }
    >
      {renderBody()}

      <DisconnectMetaAdsModal
        opened={showDisconnectModal}
        loading={isDisconnecting}
        onClose={() => setShowDisconnectModal(false)}
        onConfirm={() => disconnect()}
      />
    </Layout>
  );
};
